import {ListView} from '@github-ui/list-view'
import {
  actionsWorkflowFilePath,
  actionsWorkflowRunPath,
  branchPath,
  commitHovercardPath,
  repoAttestationDownloadPath,
  repoAttestationsIndexPath,
} from '@github-ui/paths'
import {useRoutePayload} from '@github-ui/react-core/use-route-payload'
import {ArrowLeftIcon, DownloadIcon, PackageIcon, VerifiedIcon} from '@primer/octicons-react'
import {Box, Button, Heading, Link, PageLayout, RelativeTime, Text, Truncate} from '@primer/react'
import {DataTable, Table} from '@primer/react/experimental'

import {AttestationPropertiesList} from '../components/AttestationPropertiesList'
import {VerifyAttestation} from '../components/VerifyAttestation'
import type {AttestationPayload} from '../types'

export const AttestationDetail = () => {
  // attestation payload
  const payload = useRoutePayload<AttestationPayload>()

  // run id and attempt
  const [_, runId, attempt] =
    payload.attestation.runInvocationUri.match(/\/actions\/runs\/(\d+)\/attempts\/(\d+)$/) || []

  // structure the data for certificate summary
  const certificateData = [
    {
      label: 'Build Config Digest',
      value: payload.attestation.buildConfigDigest,
    },
    {
      label: 'Build Config URI',
      value: payload.attestation.buildConfigUri,
    },
    {
      label: 'Build Signer Digest',
      value: payload.attestation.buildSignerDigest,
    },
    {
      label: 'Build Signer URI',
      value: payload.attestation.buildSignerUri,
    },
    {
      label: 'Build Trigger',
      value: payload.attestation.buildTrigger,
    },
    {
      label: 'Issuer',
      value: payload.attestation.issuer,
    },
    {
      label: 'Runner Invocation URI',
      value: payload.attestation.runInvocationUri,
    },
    {
      label: 'Runner Environment',
      value: payload.attestation.runnerEnvironment,
    },
    {
      label: 'Source Repository Digest',
      value: payload.attestation.sourceRepositoryDigest,
    },
    {
      label: 'Source Repository Identifier',
      value: payload.attestation.sourceRepositoryIdentifier,
    },
    {
      label: 'Source Repository Owner Identifier',
      value: payload.attestation.sourceRepositoryOwnerIdentifier,
    },
    {
      label: 'Source Repository Owner URI',
      value: payload.attestation.sourceRepositoryOwnerUri,
    },
    {
      label: 'Source Repository Ref',
      value: payload.attestation.sourceRepositoryRef,
    },
    {
      label: 'Source Repository URI',
      value: payload.attestation.sourceRepositoryUri,
    },
    {
      label: 'Source Repository Visibility',
      value: payload.attestation.sourceRepositoryVisibilityAtSigning,
    },
  ]

  // Add an id to each item in the certificate data for DataTable
  const certificateDataWithId = certificateData.map((item, index) => ({
    ...item,
    id: index.toString(),
  }))

  return (
    <PageLayout>
      <PageLayout.Header>
        <div>
          <Link
            sx={{color: 'fg.muted'}}
            href={repoAttestationsIndexPath({
              repo: payload.repo,
            })}
          >
            <ArrowLeftIcon size={16} /> Attestations
          </Link>
        </div>
        <Box
          sx={{
            display: 'flex',
            flexDirection: ['column', 'column', 'row'],
          }}
        >
          <Heading as="h2" sx={{display: 'flex', flexGrow: 1, alignItems: 'center', fontSize: 4, fontWeight: 'normal'}}>
            <Box sx={{color: 'success.fg', mr: 2}}>
              <VerifiedIcon size={24} />
            </Box>
            {`${payload.attestation.predicateType}`}
            <Text sx={{color: 'fg.muted', ml: 2, fontSize: 2}}>{`#${payload.attestation.id}`}</Text>
          </Heading>
          <Box sx={{display: 'flex', alignItems: 'center'}}>
            <Button
              as="a"
              data-turbo="false"
              data-testid="download-attestation-detail"
              href={repoAttestationDownloadPath({
                repo: payload.repo,
                attestationId: payload.attestation.id,
              })}
            >
              <DownloadIcon size={16} />
              <Text sx={{pl: 1}}>Download</Text>
            </Button>
          </Box>
        </Box>
      </PageLayout.Header>
      <PageLayout.Content as="div">
        <ListView variant="default" title="Title of the list" titleHeaderTag="h3" />
        <AttestationPropertiesList
          definitions={[
            {
              propertyName: 'Created',
              propertyValue: (
                <Box sx={{color: 'fg.muted', py: 2, fontSize: 1}}>
                  <RelativeTime
                    data-testid="attestation-relative-time"
                    tense="past"
                    datetime={payload.attestation.createdAt}
                  >
                    {new Date(Date.parse(payload.attestation.createdAt)).toLocaleDateString('default', {
                      month: 'short',
                      day: 'numeric',
                    })}
                  </RelativeTime>{' '}
                  ({new Date(Date.parse(payload.attestation.createdAt)).toUTCString()})
                </Box>
              ),
            },
            {
              propertyName: 'Commit',
              propertyValue: (
                <Box sx={{py: 2, fontSize: 1}}>
                  <Link
                    sx={{color: 'fg.default'}}
                    data-testid="attestation-commit-link"
                    href={branchPath({
                      owner: payload.repo.ownerLogin,
                      repo: payload.repo.name,
                      branch: payload.attestation.sourceRepositoryDigest,
                    })}
                    data-hovercard-url={commitHovercardPath({
                      owner: payload.repo.ownerLogin,
                      repo: payload.repo.name,
                      commitish: payload.attestation.sourceRepositoryDigest,
                    })}
                  >
                    {payload.attestation.sourceRepositoryDigest}
                  </Link>
                </Box>
              ),
            },
            {
              propertyName: 'Build Summary',
              propertyValue: (
                <Box sx={{py: 2, fontSize: 1}}>
                  <Truncate
                    title={`${payload.attestation.runInvocationUri}`}
                    sx={{maxWidth: 500, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                    inline
                  >
                    <Link
                      sx={{mr: 3, color: 'fg.default'}}
                      data-testid="view-build-summary"
                      href={payload.attestation.runInvocationUri}
                      underline
                    >
                      {actionsWorkflowRunPath({
                        repo: payload.repo.name,
                        owner: payload.repo.ownerLogin,
                        runId,
                        attempt,
                      })}
                    </Link>
                  </Truncate>
                </Box>
              ),
            },
            {
              propertyName: 'Workflow File',
              propertyValue: (
                <Box sx={{py: 2, fontSize: 1}}>
                  <Truncate
                    title={`${payload.attestation.buildConfigDisplayName}@${payload.attestation.sourceRepositoryRef}`}
                    sx={{maxWidth: 500, fontFamily: 'monospace', fontSize: 0, color: 'fg.default'}}
                    inline
                  >
                    <Link
                      sx={{color: 'fg.default'}}
                      data-testid="attestation-workflow-link"
                      href={actionsWorkflowFilePath({
                        repo: payload.repo.name,
                        owner: payload.repo.ownerLogin,
                        runId,
                      })}
                      underline
                    >
                      {payload.attestation.buildConfigDisplayName}@{payload.attestation.sourceRepositoryRef}
                    </Link>
                  </Truncate>
                </Box>
              ),
            },
          ]}
        />

        <Box sx={{fontSize: 0, py: 2, color: 'fg.default'}}>
          <Heading as="h2" sx={{fontSize: 1, mb: 2, mt: 2}}>
            Verify
          </Heading>
          <VerifyAttestation url={`gh attestation verify --owner ${payload.repo.ownerLogin} <filename-or-url>`} />
        </Box>

        <Box sx={{fontSize: 0, py: 2, color: 'fg.default'}}>
          <Table.Container>
            <Table.Title id="certificate-summary">Certificate Summary</Table.Title>
            <DataTable
              cellPadding="normal"
              data={certificateDataWithId}
              columns={[
                {
                  field: 'label',
                  header: 'Key',
                  rowHeader: true,
                  minWidth: 350,
                  maxWidth: 450,
                  width: 'grow',
                },
                {
                  field: 'value',
                  header: 'Value',
                  rowHeader: false,
                  minWidth: 550,
                },
              ]}
            />
          </Table.Container>
        </Box>

        <Box sx={{fontSize: 0, py: 2, color: 'fg.default'}}>
          <Table.Container>
            <Table.Title id="subjects">Subjects</Table.Title>
            <DataTable
              cellPadding="normal"
              aria-labelledby="subjects"
              aria-describedby="subjects-subtitle"
              data={payload.attestation.subjects.map((subject, index) => ({...subject, id: index}))}
              columns={[
                {
                  header: 'Subject',
                  minWidth: 350,
                  maxWidth: 450,
                  width: 'grow',
                  field: 'subjectName',
                  rowHeader: false,
                  renderCell: row => {
                    return (
                      <div>
                        <PackageIcon size={16} className={'mr-2'} verticalAlign={'middle'} />
                        <Truncate title={row.subjectName} inline sx={{maxWidth: 280, pr: 3}}>
                          {row.subjectName || '[unnamed subject]'}
                        </Truncate>
                      </div>
                    )
                  },
                },
                {
                  header: 'Subject digest',
                  field: 'subjectDigest',
                  rowHeader: false,
                  minWidth: 550,
                  renderCell: row => {
                    return (
                      <Truncate title={row.subjectDigest} inline sx={{minWidth: 540, maxWidth: 700, pr: 3}}>
                        <span className={'text-mono'}>{row.subjectDigest}</span>
                      </Truncate>
                    )
                  },
                },
              ]}
            />
          </Table.Container>
        </Box>
      </PageLayout.Content>
    </PageLayout>
  )
}

try{ AttestationDetail.displayName ||= 'AttestationDetail' } catch {}